import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { makeLogger } from '@/utils/logger/logger';
import { tokenManager } from '@/lib/TokenManager';
import { AssistantQuery } from '@/components/dashbaord/assistant/types.ts';
import { TURN_EVENT_KIND, TurnStreamEvent } from '@/hooks/assistant/turnStreamTypes.ts';
import {
	listAssistantSessionsV2HousesHouseIdAssistantSessionsGetQueryKey,
	listSessionExchangesV2HousesHouseIdAssistantSessionsSessionIdExchangesGetQueryKey,
} from '@/client/@tanstack/react-query.gen.ts';

const logger = makeLogger('assistant-stream');

const STREAM_REVEAL_INTERVAL_MS = 30;
const STREAM_REVEAL_MIN_CHARS = 6;
const STREAM_REVEAL_MAX_CHARS = 30;
const ENABLE_STREAM_REVEAL_PACING = true;

const getNextStreamDisplayLength = (displayedLength: number, targetText: string): number => {
	const remaining = targetText.length - displayedLength;
	if (remaining <= STREAM_REVEAL_MAX_CHARS) {
		return targetText.length;
	}

	const nextLineBreakIndex = targetText.indexOf('\n', displayedLength);
	if (nextLineBreakIndex !== -1) {
		const lineBreakDistance = nextLineBreakIndex - displayedLength + 1;
		if (lineBreakDistance <= STREAM_REVEAL_MAX_CHARS) {
			return nextLineBreakIndex + 1;
		}
	}

	const catchUpChunkSize = Math.ceil(remaining * 0.3);
	const nextChunkSize = Math.min(STREAM_REVEAL_MAX_CHARS, Math.max(STREAM_REVEAL_MIN_CHARS, catchUpChunkSize));
	return displayedLength + nextChunkSize;
};

/** Per-request metadata the FE attaches to each turn (mirrors the backend ClientContext). */
export interface AssistantClientContext {
	/** control: citation style ("per_claim" | "grouped"). */
	citation_mode?: string;
	/** signal: the FE route the request came from (e.g. "/records"). */
	origin_route?: string;
}

export interface UseAssistantTurnStreamProps {
	houseId: number;
	onQueryUpdate: (queryId: string, updates: Partial<AssistantQuery>) => void;
	setIsExecuting: (isLoading: boolean) => void;
	onError: (error: string) => void;
	/** Builds the per-request client_context at submit time (read fresh so it tracks UI/route state). */
	getClientContext?: () => AssistantClientContext | undefined;
}

/**
 * Hook for the v2 SSE streaming assistant endpoint.
 * Uses fetch + ReadableStream so Authorization headers can be sent properly —
 * no token-in-URL security leak unlike the legacy EventSource approach.
 *
 * Event sequence: turn_started → status* → retrieval_ready? → text_delta* → turn_completed | turn_error
 * session_id is captured from the first event and passed back on subsequent turns for multi-turn continuity.
 * `status` is multi-turn (component-family) progress only — zero of these is the normal case for an
 * ordinary RAG turn — and stops mattering the instant the first text_delta lands, since the streaming
 * answer becomes its own progress signal from then on. See docs/local/chat-feature.md §B.
 */
export const useAssistantTurnStream = ({
	houseId,
	setIsExecuting,
	onQueryUpdate,
	onError,
	getClientContext,
}: UseAssistantTurnStreamProps) => {
	const queryClient = useQueryClient();
	const sessionIdRef = useRef<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	// Read via ref at submit time so submitQuery's identity stays stable while the context tracks the
	// latest UI/route state.
	const getClientContextRef = useRef(getClientContext);
	getClientContextRef.current = getClientContext;

	const submitQuery = useCallback(
		async (query: AssistantQuery): Promise<boolean> => {
			const trimmedQuery = query.text.trim();
			if (!trimmedQuery) {
				onError('Please enter a valid query.');
				return false;
			}

			abortControllerRef.current = new AbortController();
			let accumulatedText = '';
			let completed = false;
			const streamStartedAt = performance.now();
			let textDeltaCount = 0;
			let lastTextDeltaAt = streamStartedAt;
			let displayedText = '';
			let targetText = '';
			let revealTimer: ReturnType<typeof setTimeout> | null = null;
			let resolveRevealCatchUp: (() => void) | null = null;

			const clearRevealTimer = () => {
				if (revealTimer) {
					clearTimeout(revealTimer);
					revealTimer = null;
				}
			};

			const resolveCatchUpIfReady = () => {
				if (displayedText.length < targetText.length || !resolveRevealCatchUp) return;

				resolveRevealCatchUp();
				resolveRevealCatchUp = null;
			};

			const scheduleReveal = () => {
				if (revealTimer || displayedText.length >= targetText.length) {
					resolveCatchUpIfReady();
					return;
				}

				revealTimer = setTimeout(() => {
					revealTimer = null;
					const nextLength = getNextStreamDisplayLength(displayedText.length, targetText);
					displayedText = targetText.slice(0, nextLength);
					// The reveal animation owns only the visible text — never lifecycle status. Status is
					// set to 'executing' once at submit (AssistantContext) and to 'completed'/'error' by the
					// event handlers below. If a stray reveal tick fired after completion it must not be able
					// to revert the query to 'executing' (that left the spinner stuck, pills unrendered).
					onQueryUpdate(query.id, {
						response: { text: displayedText, annotations: [] },
					});
					scheduleReveal();
				}, STREAM_REVEAL_INTERVAL_MS);
			};

			const updateRevealTarget = (nextText: string) => {
				if (!ENABLE_STREAM_REVEAL_PACING) {
					displayedText = nextText;
					targetText = nextText;
					onQueryUpdate(query.id, {
						response: { text: nextText, annotations: [] },
					});
					return;
				}

				targetText = nextText;
				scheduleReveal();
			};

			const waitForRevealCatchUp = (nextText: string): Promise<void> => {
				if (!ENABLE_STREAM_REVEAL_PACING) {
					displayedText = nextText;
					targetText = nextText;
					return Promise.resolve();
				}

				targetText = nextText;
				scheduleReveal();

				if (displayedText.length >= targetText.length) {
					return Promise.resolve();
				}

				return new Promise((resolve) => {
					resolveRevealCatchUp = resolve;
				});
			};

			try {
				// Must use the validated getter — getCurrentToken() returns the cached token
				// without checking expiry, so a stale JWT leaks through and the server 401s.
				// Axios users dodge this via the refresh interceptor; the SSE fetch bypasses it.
				const token = await tokenManager.getValidAccessToken();
				const apiUrl = import.meta.env.VITE_API_URL ?? '';

				const response = await fetch(`${apiUrl}/v2/houses/${houseId}/assistant/turn/stream`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						message: trimmedQuery,
						session_id: sessionIdRef.current ?? null,
						client_context: getClientContextRef.current?.() ?? null,
					}),
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok || !response.body) {
					const status = response.status;
					if (status === 401 || status === 403) {
						onError('Authentication error. Please try logging in again.');
					} else if (status === 429) {
						onError('Too many requests. Please wait a moment before trying again.');
					} else {
						onError('Server error. Please try again later.');
					}
					setIsExecuting(false);
					return false;
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = '';

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split('\n');
					buffer = lines.pop() ?? '';

					for (const line of lines) {
						if (!line.startsWith('data: ')) continue;
						const raw = line.slice(6).trim();
						if (!raw || raw === '[DONE]') continue;

						try {
							const event: TurnStreamEvent = JSON.parse(raw);

							// Capture session_id from the first event for multi-turn continuity
							if (!sessionIdRef.current && event.session_id) {
								sessionIdRef.current = event.session_id;
							}

							if (event.kind === TURN_EVENT_KIND.TURN_STARTED) {
								// Surface a newly accepted conversation immediately; history should not wait
								// for the first answer to finish streaming.
								onQueryUpdate(query.id, {
									queryId: event.exchange_id,
									sessionId: event.session_id,
								});
							} else if (event.kind === TURN_EVENT_KIND.TEXT_DELTA) {
								textDeltaCount += 1;
								const now = performance.now();
								logger.debug('text_delta', {
									count: textDeltaCount,
									deltaLength: event.delta.length,
									totalLength: accumulatedText.length + event.delta.length,
									msSinceStart: Math.round(now - streamStartedAt),
									msSincePreviousDelta: Math.round(now - lastTextDeltaAt),
								});
								lastTextDeltaAt = now;
								accumulatedText += event.delta;
								if (textDeltaCount === 1) {
									// The streaming answer is its own progress signal — a status label
									// alongside it would be stale. Clear on receipt, not on next render, so
									// there's no window where a now-outdated label is still shown.
									onQueryUpdate(query.id, { statusLabel: null });
								}
								updateRevealTarget(accumulatedText);
							} else if (event.kind === TURN_EVENT_KIND.STATUS) {
								logger.debug('status', { phase: event.phase, label: event.label });
								onQueryUpdate(query.id, { statusLabel: event.label });
							} else if (event.kind === TURN_EVENT_KIND.RETRIEVAL_READY) {
								logger.debug('retrieval_ready chunk_refs:', event.chunk_refs);
								onQueryUpdate(query.id, {
									chunkRefs: event.chunk_refs,
								});
							} else if (event.kind === TURN_EVENT_KIND.TURN_COMPLETED) {
								completed = true;
								logger.debug('turn_completed', {
									textDeltaCount,
									accumulatedLength: accumulatedText.length,
									finalAnswerLength: event.answer.length,
									finalAnswerMatchesStream: event.answer === accumulatedText,
									msSinceStart: Math.round(performance.now() - streamStartedAt),
									chunkRefs: event.chunk_refs,
								});
								// TODO: Source pills still visually pop in after the streamed text in some cases.
								// Consider rendering a reserved source area earlier once retrieval metadata is known.
								onQueryUpdate(query.id, {
									queryId: event.exchange_id,
									sessionId: event.session_id,
									chunkRefs: event.chunk_refs,
									status: 'executing',
								});
								await waitForRevealCatchUp(event.answer);
								// Stop the reveal animation before the terminal update. waitForRevealCatchUp can
								// resolve immediately (the normalised answer is usually shorter than the raw
								// streamed text) without consuming an in-flight timer. Reveal ticks no longer
								// touch status, so a late tick can't revert the lifecycle — this just avoids a
								// redundant text repaint after completion. Belt-and-suspenders.
								clearRevealTimer();
								onQueryUpdate(query.id, {
									queryId: event.exchange_id,
									sessionId: event.session_id,
									response: { text: event.answer, annotations: [] },
									chunkRefs: event.chunk_refs,
									sources: event.sources,
									citations: event.citations,
									answerFormatVersion: event.answer_format_version,
									webSources: event.web_sources,
									appliedCorrections: event.applied_corrections,
									pendingAction: event.pending_action ?? null,
									statusLabel: null,
									status: 'completed',
								});
								void queryClient.invalidateQueries({
									queryKey: listAssistantSessionsV2HousesHouseIdAssistantSessionsGetQueryKey({
										path: { house_id: houseId },
									}),
								});
								void queryClient.invalidateQueries({
									queryKey:
										listSessionExchangesV2HousesHouseIdAssistantSessionsSessionIdExchangesGetQueryKey(
											{
												path: { house_id: houseId, session_id: event.session_id },
											},
										),
								});
							} else if (event.kind === TURN_EVENT_KIND.TURN_ERROR) {
								onQueryUpdate(query.id, { statusLabel: null });
								onError(event.message ?? 'Assistant error.');
								setIsExecuting(false);
								return false;
							}
						} catch {
							// Malformed SSE line — skip silently
						}
					}
				}

				if (!completed) {
					if (accumulatedText) {
						await waitForRevealCatchUp(accumulatedText);
					}
					onQueryUpdate(query.id, {
						...(accumulatedText ? { response: { text: accumulatedText, annotations: [] } } : {}),
						statusLabel: null,
						status: accumulatedText ? 'completed' : 'error',
					});
					if (!accumulatedText) {
						onError('Assistant response ended before an answer was returned.');
					}
					setIsExecuting(false);
					return !!accumulatedText;
				}

				return true;
			} catch (err: unknown) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					// User cancelled — not an error
					return false;
				}
				logger.error('Failed:', err);
				onError('Failed to retrieve response from assistant.');
				setIsExecuting(false);
				return false;
			} finally {
				clearRevealTimer();
			}
		},
		[houseId, onQueryUpdate, onError, queryClient, setIsExecuting],
	);

	/**
	 * Cancels the in-flight SSE stream via AbortController.
	 * Unlike the legacy cancelRunAsync (which called a backend API), this is purely client-side.
	 */
	const cancelQuery = useCallback((_queryId: string) => {
		abortControllerRef.current?.abort();
	}, []);

	/**
	 * Resets the session so the next turn starts a fresh conversation.
	 * Call this when the user explicitly starts a new chat.
	 */
	const resetSession = useCallback(() => {
		sessionIdRef.current = null;
	}, []);

	/**
	 * Resumes a past session by setting the session_id ref.
	 * The next submitQuery call will pass this session_id to the backend.
	 */
	const resumeSession = useCallback((sessionId: string) => {
		sessionIdRef.current = sessionId;
	}, []);

	return { submitQuery, cancelQuery, resetSession, resumeSession, isStreaming: true };
};

export default useAssistantTurnStream;
